import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";

export interface Resource<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** Fetches again; keeps showing the old data meanwhile (no flash on polling). */
  reload: () => Promise<void>;
  /** Replaces the data locally, e.g. with the response of a mutation. */
  set: (data: T) => void;
}

/** Loads data for the current deps, cancels stale requests, and exposes reload/set. One concern: fetching. */
export function useResource<T>(load: (signal: AbortSignal) => Promise<T>, deps: DependencyList): Resource<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;
  const ctrl = useRef<AbortController | null>(null);

  const run = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    setLoading(true);
    try {
      const d = await loadRef.current(c.signal);
      if (!c.signal.aborted) { setData(d); setError(undefined); }
    } catch (e) {
      if (!c.signal.aborted) setError(e);
    } finally {
      if (!c.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setData(undefined);
    void run();
    return () => ctrl.current?.abort();
  }, deps);

  return { data, error, loading, reload: run, set: setData };
}
